import JSZip from 'jszip';
import type { InternalMemoFormData } from '../types/applicationForm';
import { formatSingaporeDate } from '../utils/dateTime';
import { memoBodyPlainText } from './memoBody';

const TEMPLATE_URL = '/assets/memo/int-memo-template.docx';

function xmlEscape(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Format ISO date as DD/MM/YYYY (Asia/Singapore). */
export function formatMemoDateDisplay(iso: string): string {
  return formatSingaporeDate(iso);
}

function fileSafeRef(refNo: string): string {
  return refNo.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim() || 'MEMO';
}

const WT_RE = /(<w:t(?:\s[^>]*)?>)([\s\S]*?)(<\/w:t>)/g;

function decodeWt(text: string): string {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
}

/** Word often splits {{SUBJECT}} into {{ + SUBJECT}}; merge before replace. */
function healSplitPlaceholder(xml: string, placeholder: string): string {
  if (xml.includes(placeholder)) return xml;

  const matches = [...xml.matchAll(WT_RE)];
  if (!matches.length) return xml;

  const decoded = matches.map((m) => decodeWt(m[2] ?? ''));
  for (let i = 0; i < decoded.length; i++) {
    let acc = '';
    for (let j = i; j < decoded.length; j++) {
      acc += decoded[j];
      if (acc === placeholder) {
        const first = matches[i]!;
        let open = first[1]!;
        if (
          !open.includes('xml:space') &&
          (placeholder.includes(' ') ||
            placeholder.startsWith(' ') ||
            placeholder.endsWith(' '))
        ) {
          open = open.replace('<w:t', '<w:t xml:space="preserve"');
        }
        const parts: string[] = [xml.slice(0, first.index!), `${open}${placeholder}${first[3]}`];
        let cursor = (first.index ?? 0) + first[0].length;
        for (let k = i + 1; k <= j; k++) {
          const mk = matches[k]!;
          parts.push(xml.slice(cursor, mk.index!));
          parts.push(`${mk[1]}${mk[3]}`);
          cursor = (mk.index ?? 0) + mk[0].length;
        }
        parts.push(xml.slice(cursor));
        return parts.join('');
      }
      if (acc.length > placeholder.length) break;
    }
  }
  return xml;
}

function replaceAll(haystack: string, needle: string, value: string): string {
  let xml = healSplitPlaceholder(haystack, needle);
  if (!xml.includes(needle)) return xml;
  return xml.split(needle).join(xmlEscape(value));
}

/** Form stores addressee only (e.g. "Sales Team"); Word gets "Dear Sales Team,". */
export function formatMemoSalutation(raw: string): string {
  let name = raw.trim().replace(/^dear\s+/i, '');
  name = name.replace(/,+\s*$/, '').trim();
  if (!name) return '';
  return `Dear ${name},`;
}

type RunStyle = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  highlight: boolean;
  /** Word half-points (22 = 11pt). */
  fontSize?: number;
};

type BodyBlock = {
  kind: 'paragraph' | 'bullet';
  runs: { text: string; style: RunStyle }[];
};

function defaultStyle(): RunStyle {
  return { bold: false, italic: false, underline: false, highlight: false };
}

function parseFontSizeToHalfPoints(raw: string | null | undefined): number | undefined {
  if (!raw?.trim()) return undefined;
  const t = raw.trim().toLowerCase();
  const pt = /^([\d.]+)\s*pt$/.exec(t);
  if (pt) return Math.round(parseFloat(pt[1]) * 2);
  const px = /^([\d.]+)\s*px$/.exec(t);
  if (px) return Math.round((parseFloat(px[1]) * 72) / 96 * 2);
  const n = Number.parseFloat(t);
  if (!Number.isNaN(n) && n > 0) return Math.round(n * 2);
  return undefined;
}

function mergeStyle(base: RunStyle, patch: Partial<RunStyle>): RunStyle {
  return { ...base, ...patch };
}

function collectRuns(node: Node, style: RunStyle, out: { text: string; style: RunStyle }[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    if (text) out.push({ text, style: { ...style } });
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (tag === 'br') {
    out.push({ text: '\n', style: { ...style } });
    return;
  }

  let next = style;
  if (tag === 'strong' || tag === 'b') next = mergeStyle(next, { bold: true });
  if (tag === 'em' || tag === 'i') next = mergeStyle(next, { italic: true });
  if (tag === 'u') next = mergeStyle(next, { underline: true });
  if (tag === 'mark' || el.style.backgroundColor) {
    const bg = (el.getAttribute('data-color') || el.style.backgroundColor || '').toLowerCase();
    if (tag === 'mark' || bg.includes('yellow') || bg.includes('ff0') || bg.includes('ffff00') || bg) {
      next = mergeStyle(next, { highlight: true });
    }
  }
  const fontSize =
    parseFontSizeToHalfPoints(el.style.fontSize) ??
    parseFontSizeToHalfPoints(el.getAttribute('style')?.match(/font-size:\s*([^;]+)/i)?.[1]);
  if (fontSize) next = mergeStyle(next, { fontSize });

  for (const child of Array.from(el.childNodes)) {
    collectRuns(child, next, out);
  }
}

function isHtmlBody(body: string): boolean {
  return /<[a-z][\s\S]*>/i.test(body.trim());
}

function htmlToBodyBlocks(html: string): BodyBlock[] {
  const doc = new DOMParser().parseFromString(
    `<!DOCTYPE html><html><body>${html}</body></html>`,
    'text/html',
  );
  const blocks: BodyBlock[] = [];

  const pushBlock = (kind: BodyBlock['kind'], el: HTMLElement) => {
    const runs: { text: string; style: RunStyle }[] = [];
    collectRuns(el, defaultStyle(), runs);
    // Flatten hard breaks inside a block into separate paragraphs when lone \n runs appear?
    // Keep as single paragraph; Word treats \n in a run poorly — split on \n.
    const parts: { text: string; style: RunStyle }[][] = [[]];
    for (const run of runs) {
      const chunks = run.text.split('\n');
      chunks.forEach((chunk, i) => {
        if (i > 0) parts.push([]);
        if (chunk) parts[parts.length - 1].push({ text: chunk, style: run.style });
      });
    }
    for (const part of parts) {
      if (part.length === 0) {
        blocks.push({ kind, runs: [{ text: '', style: defaultStyle() }] });
      } else {
        blocks.push({ kind, runs: part });
      }
    }
  };

  const walk = (parent: HTMLElement) => {
    for (const child of Array.from(parent.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const t = (child.textContent ?? '').trim();
        if (t) {
          blocks.push({
            kind: 'paragraph',
            runs: [{ text: child.textContent ?? '', style: defaultStyle() }],
          });
        }
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      const el = child as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (tag === 'ul') {
        for (const li of Array.from(el.children)) {
          if (li.tagName.toLowerCase() === 'li') pushBlock('bullet', li as HTMLElement);
        }
        continue;
      }
      if (tag === 'ol') {
        for (const li of Array.from(el.children)) {
          if (li.tagName.toLowerCase() === 'li') pushBlock('bullet', li as HTMLElement);
        }
        continue;
      }
      if (tag === 'p' || tag === 'div' || tag === 'h1' || tag === 'h2' || tag === 'h3') {
        pushBlock('paragraph', el);
        continue;
      }
      // Nested wrappers
      walk(el);
    }
  };

  walk(doc.body);
  if (blocks.length === 0) {
    const plain = memoBodyPlainText(html);
    if (plain) {
      return plain.split(/\n/).map((line) => ({
        kind: 'paragraph' as const,
        runs: [{ text: line, style: defaultStyle() }],
      }));
    }
    return [{ kind: 'paragraph', runs: [{ text: '', style: defaultStyle() }] }];
  }
  return blocks;
}

function plainToBodyBlocks(body: string): BodyBlock[] {
  const lines = body.replace(/\r\n/g, '\n').split('\n');
  const usable = lines.length > 0 ? lines : [''];
  return usable.map((line) => ({
    kind: 'paragraph' as const,
    runs: [{ text: line, style: defaultStyle() }],
  }));
}

const TIMES_NEW_ROMAN_RFONTS =
  '<w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:eastAsia="Times New Roman" w:cs="Times New Roman"/>';

function rPrXml(style: RunStyle): string {
  const parts: string[] = [TIMES_NEW_ROMAN_RFONTS];
  if (style.bold) parts.push('<w:b/><w:bCs/>');
  if (style.italic) parts.push('<w:i/><w:iCs/>');
  if (style.underline) parts.push('<w:u w:val="single"/>');
  if (style.highlight) parts.push('<w:highlight w:val="yellow"/>');
  // Default body size 11pt when not specified — matches template ribbon
  const halfPoints = style.fontSize ?? 22;
  parts.push(`<w:sz w:val="${halfPoints}"/><w:szCs w:val="${halfPoints}"/>`);
  return `<w:rPr>${parts.join('')}</w:rPr>`;
}

function runXml(text: string, style: RunStyle): string {
  const t = xmlEscape(text);
  const space = text.startsWith(' ') || text.endsWith(' ') || text.includes('  ')
    ? ' xml:space="preserve"'
    : '';
  return `<w:r>${rPrXml(style)}<w:t${space}>${t}</w:t></w:r>`;
}

function extractPPr(paragraphXml: string): string {
  const m = /<w:pPr\b[\s\S]*?<\/w:pPr>/.exec(paragraphXml);
  if (!m) {
    return `<w:pPr><w:rPr>${TIMES_NEW_ROMAN_RFONTS}</w:rPr></w:pPr>`;
  }
  let pPr = m[0];
  if (/<w:rFonts\b/.test(pPr)) {
    pPr = pPr.replace(/<w:rFonts\b[^/]*\/>/g, TIMES_NEW_ROMAN_RFONTS);
  } else if (/<w:rPr>/.test(pPr)) {
    pPr = pPr.replace('<w:rPr>', `<w:rPr>${TIMES_NEW_ROMAN_RFONTS}`);
  } else {
    pPr = pPr.replace('</w:pPr>', `<w:rPr>${TIMES_NEW_ROMAN_RFONTS}</w:rPr></w:pPr>`);
  }
  return pPr;
}

/** Find `<w:p …>` start (not `<w:pPr>` / self-closing `<w:p …/>`) that contains markerIdx. */
function findParagraphBounds(
  xml: string,
  markerIdx: number,
): { start: number; end: number } | null {
  const re = /<w:p\b[^>]*>/g;
  let start = -1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    if (m.index > markerIdx) break;
    // Skip empty self-closing paragraphs: <w:p … />
    if (/\/\s*>$/.test(m[0])) continue;
    start = m.index;
  }
  if (start < 0) return null;
  const endTag = '</w:p>';
  const end = xml.indexOf(endTag, markerIdx);
  if (end < 0) return null;
  return { start, end: end + endTag.length };
}

function blockToParagraphXml(block: BodyBlock, basePPr: string): string {
  let pPr = basePPr;
  const runsXml: string[] = [];
  if (block.kind === 'bullet') {
    // Indent + bullet prefix (avoid numbering.xml)
    if (/<w:ind\b/.test(pPr)) {
      pPr = pPr.replace(/<w:ind\b[^/]*\/>/, '<w:ind w:left="720" w:hanging="360"/>');
    } else if (pPr) {
      pPr = pPr.replace('</w:pPr>', '<w:ind w:left="720" w:hanging="360"/></w:pPr>');
    } else {
      pPr = '<w:pPr><w:ind w:left="720" w:hanging="360"/></w:pPr>';
    }
    runsXml.push(runXml('• ', defaultStyle()));
  }
  for (const run of block.runs) {
    runsXml.push(runXml(run.text, run.style));
  }
  if (runsXml.length === (block.kind === 'bullet' ? 1 : 0)) {
    runsXml.push(runXml('', defaultStyle()));
  }
  return `<w:p>${pPr}${runsXml.join('')}</w:p>`;
}

function blocksToParagraphsXml(blocks: BodyBlock[], basePPr: string): string {
  return blocks.map((b) => blockToParagraphXml(b, basePPr)).join('');
}

/**
 * Expand {{BODY}} into Word paragraphs (plain or rich HTML).
 * Template keeps {{SALUTATION}} and {{BODY}} in the same <w:p> — preserve salutation,
 * then insert body paragraphs after that paragraph.
 */
function expandBodyPlaceholder(docXml: string, body: string): string {
  const marker = '{{BODY}}';
  docXml = healSplitPlaceholder(docXml, marker);
  const idx = docXml.indexOf(marker);
  if (idx < 0) return docXml;

  const bounds = findParagraphBounds(docXml, idx);
  if (!bounds) {
    return replaceAll(docXml, marker, memoBodyPlainText(body));
  }

  const paragraph = docXml.slice(bounds.start, bounds.end);
  const basePPr = extractPPr(paragraph);
  const blocks = isHtmlBody(body) ? htmlToBodyBlocks(body) : plainToBodyBlocks(body);
  const paragraphsXml = blocksToParagraphsXml(blocks, basePPr);

  // Same paragraph as salutation: keep greeting, remove BODY marker, append body paras after.
  if (paragraph.includes('{{SALUTATION}}')) {
    const kept = paragraph.split(marker).join('');
    return docXml.slice(0, bounds.start) + kept + paragraphsXml + docXml.slice(bounds.end);
  }

  return docXml.slice(0, bounds.start) + paragraphsXml + docXml.slice(bounds.end);
}

/** 20 mm stamp in EMUs (English Metric Units: 1 mm = 36000). */
const MEMO_STAMP_EMU = Math.round(20 * 36000);

function buildInlineStampDrawingXml(relId: string, emu: number): string {
  return (
    `<w:r>` +
    `<w:drawing>` +
    `<wp:inline distT="0" distB="0" distL="0" distR="0">` +
    `<wp:extent cx="${emu}" cy="${emu}"/>` +
    `<wp:effectExtent l="0" t="0" r="0" b="0"/>` +
    `<wp:docPr id="91001" name="Creator stamp"/>` +
    `<wp:cNvGraphicFramePr>` +
    `<a:graphicFrameLocks xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" noChangeAspect="1"/>` +
    `</wp:cNvGraphicFramePr>` +
    `<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">` +
    `<a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
    `<pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
    `<pic:nvPicPr>` +
    `<pic:cNvPr id="91000" name="creator-stamp.png"/>` +
    `<pic:cNvPicPr/>` +
    `</pic:nvPicPr>` +
    `<pic:blipFill>` +
    `<a:blip r:embed="${relId}"/>` +
    `<a:stretch><a:fillRect/></a:stretch>` +
    `</pic:blipFill>` +
    `<pic:spPr>` +
    `<a:xfrm>` +
    `<a:off x="0" y="0"/>` +
    `<a:ext cx="${emu}" cy="${emu}"/>` +
    `</a:xfrm>` +
    `<a:prstGeom prst="rect"><a:avLst/></a:prstGeom>` +
    `</pic:spPr>` +
    `</pic:pic>` +
    `</a:graphicData>` +
    `</a:graphic>` +
    `</wp:inline>` +
    `</w:drawing>` +
    `</w:r>`
  );
}

/** Bounds of the <w:r>…</w:r> that contains marker (not a prior run). */
function findRunBoundsContaining(xml: string, marker: string): { start: number; end: number } {
  const markerIdx = xml.indexOf(marker);
  if (markerIdx < 0) {
    throw new Error(`Could not find ${marker} placeholder to embed creator stamp.`);
  }
  const runOpenRe = /<w:r(?:\s[^>]*)?>/g;
  let start = -1;
  let m: RegExpExecArray | null;
  while ((m = runOpenRe.exec(xml)) !== null) {
    if (m.index > markerIdx) break;
    start = m.index;
  }
  if (start < 0) {
    throw new Error('Could not locate Word run for Issued-by stamp.');
  }
  const endTag = '</w:r>';
  const end = xml.indexOf(endTag, markerIdx);
  if (end < 0) {
    throw new Error('Could not locate end of Word run for Issued-by stamp.');
  }
  return { start, end: end + endTag.length };
}

/** Embed 20×20 mm creator seal as the next line under "Issued by:" (replaces {{ISSUED_BY}}). */
async function embedIssuedByStampInDocx(zip: JSZip, stampName: string): Promise<void> {
  const { renderCreatorNameStampPng } = await import('./creatorNameStamp');
  const png = await renderCreatorNameStampPng(stampName);
  zip.file('word/media/creator-stamp.png', png);

  const relsPath = 'word/_rels/document.xml.rels';
  const relsFile = zip.file(relsPath);
  if (!relsFile) throw new Error('Memo template missing document.xml.rels.');
  let rels = await relsFile.async('string');
  const ids = [...rels.matchAll(/Id="rId(\d+)"/g)].map((m) => Number(m[1]));
  const nextId = `rId${Math.max(0, ...ids) + 1}`;
  if (!rels.includes('</Relationships>')) {
    throw new Error('Memo template relationships file is invalid.');
  }
  rels = rels.replace(
    '</Relationships>',
    `<Relationship Id="${nextId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/creator-stamp.png"/></Relationships>`,
  );
  zip.file(relsPath, rels);

  const docPath = 'word/document.xml';
  const docFile = zip.file(docPath);
  if (!docFile) throw new Error('Memo template missing document.xml.');
  let docXml = healSplitPlaceholder(await docFile.async('string'), '{{ISSUED_BY}}');
  const { start, end } = findRunBoundsContaining(docXml, '{{ISSUED_BY}}');
  docXml =
    docXml.slice(0, start) + buildInlineStampDrawingXml(nextId, MEMO_STAMP_EMU) + docXml.slice(end);
  zip.file(docPath, docXml);
}

export async function buildMemoDocxBlob(
  form: InternalMemoFormData,
  applicationNo?: string,
  options: {
    /** When set, embed a 20 mm name seal under Issued by (inline in Word). */
    issuedByStampName?: string;
  } = {},
): Promise<Blob> {
  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) throw new Error('Failed to load Internal Memorandum template.');
  const zip = await JSZip.loadAsync(await res.arrayBuffer());

  const stampName = options.issuedByStampName?.replace(/\s+/g, ' ').trim() || '';
  if (stampName) {
    await embedIssuedByStampInDocx(zip, stampName);
  }

  const refNo = (applicationNo || form.ref_no || '').trim();
  const values: Record<string, string> = {
    '{{REF_NO}}': refNo,
    '{{TO}}': form.to.trim(),
    '{{CC}}': form.cc.trim(),
    // From field removed from the form — Word header uses Issued by.
    '{{FROM_LINE}}': form.issued_by.trim() || form.from_line.trim(),
    '{{DATE}}': formatMemoDateDisplay(form.date) || form.date.trim(),
    '{{SUBJECT}}': form.subject.trim(),
    // Greeting follows To (Salutation field removed from the form).
    '{{SALUTATION}}': formatMemoSalutation(form.to),
    // Stamp already replaced the placeholder run when issuedByStampName is set.
    '{{ISSUED_BY}}': stampName ? '' : form.issued_by.trim(),
    '{{ACKNOWLEDGED_BY}}': '',
  };

  const patchXml = (xml: string, path: string): string => {
    let next = path === 'word/document.xml' ? expandBodyPlaceholder(xml, form.body) : xml;
    for (const [key, value] of Object.entries(values)) {
      next = replaceAll(next, key, value);
    }
    return next;
  };

  for (const path of [
    'word/document.xml',
    'word/header1.xml',
    'word/header2.xml',
    'word/header3.xml',
  ]) {
    const file = zip.file(path);
    if (!file) continue;
    const xml = await file.async('string');
    zip.file(path, patchXml(xml, path));
  }

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
  });
}

export async function downloadMemoDocx(
  form: InternalMemoFormData,
  applicationNo?: string,
): Promise<void> {
  const blob = await buildMemoDocxBlob(form, applicationNo);
  const name = `${fileSafeRef(applicationNo || form.ref_no)}.docx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export async function buildMemoDocxBase64(
  form: InternalMemoFormData,
  applicationNo?: string,
  options: { issuedByStampName?: string } = {},
): Promise<string> {
  const blob = await buildMemoDocxBlob(form, applicationNo, options);
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
